import React, { useState, useEffect, ReactNode, TouchEvent, useRef } from "react";
import useMobile from "@/app/hooks/use-mobile";
import { Info, Minus, Plus } from "lucide-react";
import { formatIndianCurrency, setRedirectTo } from "@/app/utils/helper";
import { CustomizableSlider } from "@/app/components/shared/Custom-Sldier";
import { AMZ_VOUCHER, MAX_EARNING } from "@/app/utils/constants";
import { ButtonSize, ButtonVariant, IconButton } from "@/stories/icon-button/icon-button";
import { RegularButton, ButtonSize as RegularButtonSize } from "@/stories/button/button";


interface SwipeableDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    isAuthenticated?: boolean;
}

interface ScrollableButtonReferProps {
    isAuthenticated?: boolean;
}

const SwipeableDrawer: React.FC<SwipeableDrawerProps> = ({ isOpen, onClose, children }) => {
    const [startY, setStartY] = useState<number | null>(null);
    const [currentY, setCurrentY] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const bodyRef = useRef<HTMLBodyElement | null>(null);

    useEffect(() => {
        bodyRef.current = document.querySelector("body");

        if (isOpen) {
            bodyRef.current?.classList.add("overflow-hidden");
        } else {
            bodyRef.current?.classList.remove("overflow-hidden");
        }

        return () => {
            bodyRef.current?.classList.remove("overflow-hidden");
        };
    }, [isOpen]);

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
        setStartY(e.touches[0].clientY);
        setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
        if (!startY) return;
        const currentTouch: number = e.touches[0].clientY;
        const diff: number = currentTouch - startY;
        if (diff > 0) {
            setCurrentY(diff);
        }
    };

    const handleTouchEnd = (): void => {
        setIsDragging(false);
        if (currentY > 100) {
            onClose();
        }
        setCurrentY(0);
        setStartY(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div
                className="fixed inset-0 bg-gray-700 bg-opacity-50 transition-opacity"
                onClick={onClose}
            />
            <div
                className={`fixed bottom-0 left-0 right-0 transform rounded-t-3xl bg-white shadow-lg transition-transform duration-300 ease-out ${
                    isOpen && !isDragging ? "translate-y-0 delay-150" : "translate-y-full"
                }`}
                style={{
                    transform: `translateY(${isDragging ? currentY : 0}px)`,
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                <div
                    className="w-full cursor-grab active:cursor-grabbing"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="mx-auto my-4 h-1 w-16 rounded-full bg-gray-300" />
                </div>
                <div className="px-4">{children}</div>
            </div>
        </div>
    );
};

const ScrollableButtonRefer: React.FC<ScrollableButtonReferProps> = ({isAuthenticated}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const { isMobile } = useMobile();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [amtValue, setAmtValue] = useState<number>(100000);
    const [noOfFriends, setNoOfFriends] = useState<number>(5);

    const handleFriendsChange = (increment: boolean) => {
        setNoOfFriends((prev) => Math.max(0, prev + (increment ? 1 : -1)));
    };

    const calculateReturns = React.useCallback(() => {
        if (amtValue <= 0 || noOfFriends <= 0) {
            return 0;
        }

        const transactionBenefit = 0.01 * amtValue;
        const totalBenefitPerUser = transactionBenefit + AMZ_VOUCHER;

        return Math.min(totalBenefitPerUser, MAX_EARNING) * noOfFriends;
    }, [amtValue, noOfFriends]);


    if (!isMobile) return null;
    return (
        <>
            <div className="fixed bottom-0 left-0 z-40 w-full">
                <div className="w-full bg-white px-4 py-3 shadow-md">
                    <div className="container mx-auto flex items-center justify-center">
                        <button
                            ref={buttonRef}
                            className="w-full rounded-full bg-orange-500 py-3 text-center font-medium text-white disabled:bg-orange-200"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            Referral Calculator
                        </button>
                    </div>
                </div>
            </div>

            <SwipeableDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} isAuthenticated={isAuthenticated}>
                <div className="space-y-6 text-center">
                    <div className="flex flex-1 flex-col items-center justify-center rounded-3xl bg-white">
                        {" "}
                        <div className="w-25 border-b border-dotted border-gray-300 px-s9 py-s9 text-center text-3xl font-semibold leading-[23px] text-neutral-900">
                            Calculate your earnings
                        </div>
                        <div className="flex w-full flex-col flex-wrap sm:flex-row">
                            <div className="flex flex-1 flex-col gap-s11 px-s13 py-s10 md:py-s14">
                                <div className="flex flex-col items-center justify-center gap-s4">
                                    <div className="text-sm leading-[19.6px] text-neutral-500">
                                        Total number of friends you refer
                                    </div>
                                    <div className="flex items-center justify-center gap-s8">
                                        <IconButton
                                            icon={<Minus />}
                                            size={ButtonSize.Extrasmall}
                                            disabled={noOfFriends === 0}
                                            onClick={() => handleFriendsChange(false)}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="bg-white"
                                            label="-"
                                        />
                                        <span className="tracking-[]-1.4px text-3xl font-bold leading-[24px] text-neutral-900">
                                            {noOfFriends}
                                        </span>
                                        <IconButton
                                            icon={<Plus />}
                                            onClick={() => handleFriendsChange(true)}
                                            size={ButtonSize.Extrasmall}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="bg-white"
                                            label="+"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col items-center justify-center gap-s5 ">
                                    <div className="text-center">
                                        <div className="text-sm leading-[19.6px] text-neutral-500">
                                            Avg. amount invested by each friend
                                        </div>
                                        <h5 className="text-3xl font-bold leading-[-1.4px] text-neutral-900">
                                            {formatIndianCurrency(amtValue)}
                                        </h5>
                                    </div>

                                    <CustomizableSlider
                                        value={[amtValue]}
                                        onValueChange={(value: number[]) => {
                                            setAmtValue(value[0]);
                                        }}
                                        defaultValue={[10000]}
                                        max={10000000}
                                        step={1}
                                        className="h-2 rounded-lg bg-gray-200"
                                        rangeClassName="bg-brand-primary-500"
                                        thumbClassName="border-grey-200"
                                    />
                                    
                                </div>
                            </div>
                            <div className="mt-s6 flex flex-1 items-center justify-center sm:mr-s6">
                                <div className="flex h-full flex-col min-w-[85%] max-w-[85%] items-center justify-center bg-white py-s6 md:min-w-[90%] md:max-w-[90%] md:px-s13 border-t border-dotted border-gray-300">
                                    <div className="flex flex-col items-center gap-s4 pb-10 px-9">
                                        <div className="relative flex items-center justify-center gap-s3">
                                            <div className="text-nowrap text-xs leading-[16.8px] text-neutral-500">
                                                YOUR EARNING
                                            </div>
                                            <div className="group relative">
                                                <Info
                                                    width="16"
                                                    height="16"
                                                    className="text-neutral-300"
                                                />
                                                <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                    Earn up to ₹10,000 for every new referral
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-5xl font-bold leading-[40px] tracking-[-2.4px] text-neutral-900">
                                            {formatIndianCurrency(calculateReturns())}
                                        </div>
                                    </div>
                                     <RegularButton
                                        noIcon
                                        showRightIcon={false}
                                        size={RegularButtonSize.Large}
                                        variant={ ButtonVariant.PrimaryDefault}
                                        className="max-w-1/2 w-full sm:w-3/5 md:max-w-fit mb-12"
                                        label="Refer now"
                                        onClick={() => {
                                            setRedirectTo(window.location.pathname);
                                            if (isAuthenticated) {
                                                window.location.href = window.location.pathname;
                                            } else {
                                                window.location.href = "/login";
                                            }
                                        }}
                                    />
   
                                </div>
                            </div>
                        </div>
                    </div> 
                </div>
            </SwipeableDrawer>
        </>
    );
};

export default ScrollableButtonRefer;
